// sort-imports-ignore
import "@/styles/main.css";
import "@workspace/ui/globals.css";

import { Analytics } from "@vercel/analytics/next";
import { captureBrowserEvent } from "@workspace/analytics/browser";
import { initLog } from "@workspace/logger/client";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import { Inter, JetBrains_Mono } from "next/font/google";
import Head from "next/head";
import { useRouter } from "next/router";
import type { Session } from "next-auth/";
import { DefaultSeo } from "next-seo";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { MapProvider } from "react-map-gl";

const inter = Inter({
  subsets: ["latin"],
  variable: "--font-inter",
  display: "swap",
});

const jetbrainsMono = JetBrains_Mono({
  subsets: ["latin"],
  weight: ["400", "500", "600"],
  variable: "--font-jetbrains-mono",
  display: "swap",
});

import { GoogleOneTap } from "@/components/auth/google-one-tap";
import { ErrorBoundary } from "@/components/error-boundary";
import { RootLayout } from "@/components/layouts/root";
import { AnalyticsScriptsProvider } from "@/components/providers/analytics-scripts-provider";
import { NotificationProvider } from "@/components/providers/message-notification-provider";
import { PilotFlagProvider } from "@/components/providers/pilot-flag-provider";
import { PostHogIdentityProvider } from "@/components/providers/posthog-identity-provider";
import { PostHogProvider } from "@/components/providers/posthog-provider";
import { QueryProvider } from "@/components/providers/query-provider";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { ToastProvider } from "@/components/providers/toast-provider";
import { TransitionProvider } from "@/components/providers/transition-provider";
import { UserSessionProvider } from "@/components/providers/user-session-provider";
import { ServiceWorkerRegistration } from "@/components/service-worker-registration";
import { TailwindIndicator } from "@/components/tailwind-indicator";
import SEO from "@/config/next-seo";

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps<{
  session: Session | null | undefined;
  pilotSelfServeLocked?: boolean;
  fixedJobTemplatesEnabled?: boolean;
}> & {
  Component: NextPageWithLayout;
};

function App({
  Component,
  pageProps: {
    session,
    pilotSelfServeLocked,
    fixedJobTemplatesEnabled,
    ...pageProps
  },
}: AppPropsWithLayout) {
  const oldUrlRef = useRef("");
  const router = useRouter();

  const getLayout = Component.getLayout || ((page) => <>{page}</>);

  useEffect(() => {
    initLog({
      service: "web",
      transport: {
        enabled: true,
        endpoint: "/api/_evlog/ingest",
      },
    });

    const handleRouteChange = () => captureBrowserEvent("$pageview");

    const handleRouteChangeStart = () =>
      captureBrowserEvent("$pageleave", {
        $current_url: oldUrlRef.current,
      });

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeStart", handleRouteChangeStart);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router.events]);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      const main = document.getElementById("main-content");
      if (main && !main.contains(document.activeElement)) {
        main.setAttribute("tabindex", "-1");
        main.focus();
        main.removeAttribute("tabindex");
      }
    };

    router.events.on("routeChangeComplete", handleRouteChangeComplete);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router.events]);

  return (
    <>
      <Head>
        <meta content="width=device-width, initial-scale=1" name="viewport" />
      </Head>
      <Analytics />
      <AnalyticsScriptsProvider />
      <ServiceWorkerRegistration />
      <DefaultSeo {...SEO} />
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        storageKey="vacwork-theme"
      >
        <div className={`${inter.variable} ${jetbrainsMono.variable}`}>
          <UserSessionProvider session={session}>
            <GoogleOneTap />
            <QueryProvider>
              <PilotFlagProvider
                fixedJobTemplatesEnabled={fixedJobTemplatesEnabled}
                pilotSelfServeLocked={pilotSelfServeLocked}
              >
                <NuqsAdapter>
                  <PostHogProvider>
                    <PostHogIdentityProvider>
                      <TransitionProvider>
                        <ErrorBoundary>
                          <MapProvider>
                            <RootLayout>
                              <NotificationProvider>
                                {getLayout(<Component {...pageProps} />)}
                              </NotificationProvider>
                              <TailwindIndicator />
                              <ToastProvider />
                            </RootLayout>
                          </MapProvider>
                        </ErrorBoundary>
                      </TransitionProvider>
                    </PostHogIdentityProvider>
                  </PostHogProvider>
                </NuqsAdapter>
              </PilotFlagProvider>
            </QueryProvider>
          </UserSessionProvider>
        </div>
      </ThemeProvider>
    </>
  );
}

export default App;
